import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import {
  TrendingUp,
  Plus,
  Trash2,
  ToggleLeft,
  ToggleRight,
  Package,
  ImagePlus,
  Image as ImageIcon,
  X,
} from "lucide-react";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { cacheGet, cacheSet, cacheKey } from "../../lib/cache";
import UsageTour from "../../components/UsageTour";
import BackButton from "../../components/BackButton";
import { Spinner, Alert, formatRupiah } from "../../components/ui";

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
const MAX_PHOTO_BYTES = 5 * 1024 * 1024;
const ALLOWED_PHOTO_TYPES = ["image/jpeg", "image/png", "image/webp"];

function coverSrc(ws) {
  if (!ws?.cover_photo_url) return null;
  if (/^https?:\/\//.test(ws.cover_photo_url)) return ws.cover_photo_url;
  return `${BACKEND_URL}${ws.cover_photo_url}`;
}

export default function WorkerServices() {
  const { user } = useAuth();
  const [myServices, setMyServices] = useState(() => cacheGet(cacheKey(user?.id, "ws-mine")).data || []);
  const [allServices, setAllServices] = useState(() => cacheGet("catalog-services").data || []);
  const [categories, setCategories] = useState(() => cacheGet("catalog-categories").data || []);
  const [loading, setLoading] = useState(
    () => myServices.length === 0 || allServices.length === 0
  );
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [customPrice, setCustomPrice] = useState("");
  const [descOverride, setDescOverride] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [msg, setMsg] = useState(null);
  const [filterCat, setFilterCat] = useState("");
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [photoUploadingId, setPhotoUploadingId] = useState(null);
  const cardFileRefs = useRef({});

  const fetchData = async () => {
    try {
      const [myRes, allRes, catRes] = await Promise.all([
        api.get("/api/worker/services"),
        api.get("/api/services"),
        api.get("/api/categories"),
      ]);
      setMyServices(myRes.data);
      setAllServices(allRes.data);
      setCategories(catRes.data);
      cacheSet(cacheKey(user?.id, "ws-mine"), myRes.data);
      cacheSet("catalog-services", allRes.data, 300000);
      cacheSet("catalog-categories", catRes.data, 300000);
    } catch (err) {
      console.error("Failed to load services:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [user?.id]);

  const myServiceIds = new Set(myServices.map((s) => s.service?.id).filter(Boolean));
  const availableServices = allServices.filter(
    (s) => !myServiceIds.has(s.id)
  );
  const filteredAvailable = filterCat
    ? availableServices.filter((s) => s.category?.slug === filterCat)
    : availableServices;

  const handlePhotoSelect = (file) => {
    if (!file) return;
    if (!ALLOWED_PHOTO_TYPES.includes(file.type)) {
      setMsg({ type: "error", text: "Format foto harus JPEG, PNG, atau WebP." });
      return;
    }
    if (file.size > MAX_PHOTO_BYTES) {
      setMsg({ type: "error", text: "Ukuran foto maksimal 5MB." });
      return;
    }
    if (photoPreview) URL.revokeObjectURL(photoPreview);
    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
  };

  const resetAddForm = () => {
    setSelectedService(null);
    setCustomPrice("");
    setDescOverride("");
    setPhotoFile(null);
    if (photoPreview) URL.revokeObjectURL(photoPreview);
    setPhotoPreview(null);
  };

  const handleAdd = async () => {
    if (!selectedService) return;
    setSubmitting(true);
    setMsg(null);
    try {
      const { data } = await api.post("/api/worker/services", {
        service_id: selectedService.id,
        custom_price: customPrice ? parseInt(customPrice) : null,
        description_override: descOverride || null,
      });
      // Foto cover opsional: upload setelah jasa dibuat
      const newId = data?.id;
      if (photoFile && newId) {
        const form = new FormData();
        form.append("photo", photoFile);
        await api.post(`/api/worker/services/${newId}/photo`, form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      setMsg({ type: "success", text: "Berhasil menambahkan layanan!" });
      setShowAddModal(false);
      resetAddForm();
      fetchData();
    } catch (err) {
      setMsg({ type: "error", text: err.response?.data?.detail || "Gagal menambahkan layanan" });
    } finally {
      setSubmitting(false);
    }
  };

  const handlePhotoUpdate = async (wsId, file) => {
    if (!file) return;
    if (!ALLOWED_PHOTO_TYPES.includes(file.type)) {
      setMsg({ type: "error", text: "Format foto harus JPEG, PNG, atau WebP." });
      return;
    }
    if (file.size > MAX_PHOTO_BYTES) {
      setMsg({ type: "error", text: "Ukuran foto maksimal 5MB." });
      return;
    }
    setPhotoUploadingId(wsId);
    try {
      const form = new FormData();
      form.append("photo", file);
      await api.post(`/api/worker/services/${wsId}/photo`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setMsg({ type: "success", text: "Foto katalog berhasil diperbarui." });
      fetchData();
    } catch (err) {
      setMsg({ type: "error", text: err.response?.data?.detail || "Gagal mengunggah foto." });
    } finally {
      setPhotoUploadingId(null);
    }
  };

  const handlePhotoDelete = async (wsId) => {
    if (!window.confirm("Hapus foto katalog jasa ini?")) return;
    try {
      await api.delete(`/api/worker/services/${wsId}/photo`);
      setMsg({ type: "success", text: "Foto katalog dihapus." });
      fetchData();
    } catch (err) {
      setMsg({ type: "error", text: err.response?.data?.detail || "Gagal menghapus foto." });
    }
  };

  const handleDelete = async (wsId) => {
    if (!window.confirm("Yakin ingin menghapus layanan ini?")) return;
    try {
      await api.delete(`/api/worker/services/${wsId}`);
      setMsg({ type: "success", text: "Layanan berhasil dihapus." });
      fetchData();
    } catch (err) {
      setMsg({ type: "error", text: "Gagal menghapus layanan." });
    }
  };

  const handleToggle = async (wsId, current) => {
    try {
      await api.put(`/api/worker/services/${wsId}`, { is_available: !current });
      fetchData();
    } catch (err) {
      console.error(err);
    }
  };

  if (loading) return <Spinner />;

  return (
    <div className="animate-fade-up grid place-items-center py-8">
      {!loading && (
        <UsageTour
          tourKey="tour-worker-services-v1"
          continueKey="tour-worker-services"
          auto={false}
          steps={
            myServices.length > 0
              ? [
                  {
                    selector: `my-service-${myServices[0].id}`,
                    title: "Kartu jasamu 👆",
                    text: "Dari sini atur harga, aktif/nonaktif, hapus, dan pasang foto. Foto asli menaikkan kepercayaan pelanggan drastis.",
                  },
                  {
                    selector: "add-service-btn",
                    title: "Tambah lagi di sini",
                    text: "Makin banyak jasa ditawarkan = makin besar peluang dipesan. Ulangi: Tambah → harga → foto.",
                  },
                ]
              : [
                  {
                    selector: "add-service-btn",
                    title: "Mulai dari sini ➕",
                    text: "Kamu belum punya jasa. Pencet Tambah Jasa → pilih dari katalog → atur harga → pasang foto. Tanpa ini, pelanggan tidak bisa memesanmu!",
                  },
                ]
          }
        />
      )}
      <div className="container space-y-6">
        <BackButton to="/worker" />
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-brand-800">Jasa Saya</h1>
            <p className="text-brand-400 mt-1">Kelola layanan yang Anda tawarkan</p>
          </div>
          <button
            onClick={() => setShowAddModal(true)}
            className="btn-primary !py-2.5 !px-4 text-sm"
            data-testid="add-service-btn"
          >
            <Plus className="h-4 w-4" /> Tambah Jasa
          </button>
        </div>

        {msg && (
          <Alert kind={msg.type} testid="worker-services-msg">
            {msg.text}
          </Alert>
        )}

        {/* My Services */}
        {myServices.length > 0 ? (
          <div className="space-y-3">
            <h2 className="text-brand-800">Layanan Aktif ({myServices.length})</h2>
            {myServices.map((ws) => (
              <div
                key={ws.id}
                className="card p-4 hover:border-brand-200 transition"
                data-testid={`my-service-${ws.id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  {/* Foto cover katalog */}
                  <div className="flex-shrink-0">
                    {coverSrc(ws) ? (
                      <img
                        src={coverSrc(ws)}
                        alt={ws.service?.name || "Foto jasa"}
                        className="h-20 w-20 rounded-xl object-cover border border-brand-100"
                        loading="lazy"
                      />
                    ) : (
                      <div className="h-20 w-20 rounded-xl bg-brand-50 grid place-items-center border border-dashed border-brand-200">
                        <ImageIcon className="h-6 w-6 text-brand-300" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-brand-800">{ws.service?.name || "Layanan"}</p>
                    {ws.description_override && (
                      <p className="text-sm text-brand-500 mt-1">{ws.description_override}</p>
                    )}
                    <div className="flex items-center gap-3 mt-2">
                      <span className="text-lg font-extrabold text-brand-800">
                        {formatRupiah(ws.custom_price || ws.service?.base_price)}
                      </span>
                      {ws.custom_price && (
                        <span className="text-xs text-brand-400">Harga kustom</span>
                      )}
                    </div>
                    {/* Kelola foto katalog */}
                    <div className="flex items-center gap-2 mt-2">
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        className="hidden"
                        ref={(el) => { cardFileRefs.current[ws.id] = el; }}
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          e.target.value = "";
                          if (f) handlePhotoUpdate(ws.id, f);
                        }}
                      />
                      <button
                        onClick={() => cardFileRefs.current[ws.id]?.click()}
                        disabled={photoUploadingId === ws.id}
                        className="text-xs font-medium text-brand-600 hover:text-brand-800 flex items-center gap-1"
                      >
                        <ImagePlus className="h-3.5 w-3.5" />
                        {photoUploadingId === ws.id
                          ? "Mengunggah..."
                          : ws.has_cover_photo
                            ? "Ganti foto"
                            : "Tambah foto"}
                      </button>
                      {ws.has_cover_photo && (
                        <button
                          onClick={() => handlePhotoDelete(ws.id)}
                          className="text-xs font-medium text-red-400 hover:text-red-600"
                        >
                          Hapus foto
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleToggle(ws.id, ws.is_available)}
                      className="p-2 rounded-lg hover:bg-brand-50 transition"
                      title={ws.is_available ? "Nonaktifkan" : "Aktifkan"}
                      data-testid={`toggle-service-${ws.id}`}
                    >
                      {ws.is_available ? (
                        <ToggleRight className="h-6 w-6 text-success-500" />
                      ) : (
                        <ToggleLeft className="h-6 w-6 text-brand-300" />
                      )}
                    </button>
                    <button
                      onClick={() => handleDelete(ws.id)}
                      className="p-2 rounded-lg hover:bg-red-50 text-red-400 hover:text-red-600 transition"
                      title="Hapus"
                      data-testid={`delete-service-${ws.id}`}
                    >
                      <Trash2 className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="card p-10 text-center">
            <TrendingUp className="h-12 w-12 text-brand-200 mx-auto" />
            <p className="font-bold text-brand-800 mt-4">Belum ada jasa</p>
            <p className="text-brand-400 mt-1">
              Mulai tambahkan layanan untuk menerima pesanan dari pelanggan.
            </p>
          </div>
        )}

        {/* Add Service Modal */}
        {showAddModal && (
          <div className="fixed inset-0 bg-black/40 z-50 grid place-items-center p-4">
            <div className="bg-white rounded-3xl w-full max-w-lg max-h-[85vh] overflow-auto p-6 space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-brand-800 text-lg">Tambah Jasa Baru</h2>
                <button
                  onClick={() => { setShowAddModal(false); resetAddForm(); }}
                  className="text-brand-400 hover:text-brand-600 text-xl"
                >
                  &times;
                </button>
              </div>

              {/* Category filter */}
              <select
                value={filterCat}
                onChange={(e) => { setFilterCat(e.target.value); setSelectedService(null); }}
                className="w-full rounded-xl border-2 border-brand-100 px-3 py-2.5 text-sm text-brand-800 focus:border-brand-500 focus:outline-none"
              >
                <option value="">Semua Kategori</option>
                {categories.map((c) => (
                  <option key={c.slug} value={c.slug}>{c.name}</option>
                ))}
              </select>

              {/* Service list */}
              <div className="space-y-2 max-h-60 overflow-auto">
                {filteredAvailable.length > 0 ? (
                  filteredAvailable.map((svc) => (
                    <button
                      key={svc.id}
                      onClick={() => setSelectedService(svc)}
                      className={`w-full text-left p-3 rounded-xl border-2 transition ${
                        selectedService?.id === svc.id
                          ? "border-brand-500 bg-brand-50"
                          : "border-brand-100 hover:border-brand-200"
                      }`}
                    >
                      <p className="font-bold text-brand-800 text-sm">{svc.name}</p>
                      <p className="text-xs text-brand-400">
                        {svc.category?.name} — {formatRupiah(svc.base_price)}
                      </p>
                    </button>
                  ))
                ) : (
                  <p className="text-sm text-brand-400 text-center py-4">
                    Tidak ada layanan tersedia
                  </p>
                )}
              </div>

              {selectedService && (
                <div className="space-y-3 pt-2 border-t border-brand-50">
                  <p className="text-sm font-bold text-brand-800">
                    Ditambahkan: {selectedService.name}
                  </p>
                  <div>
                    <label className="text-sm text-brand-500">Harga Kustom (opsional)</label>
                    <input
                      type="number"
                      value={customPrice}
                      onChange={(e) => setCustomPrice(e.target.value)}
                      placeholder={`Default: ${formatRupiah(selectedService.base_price)}`}
                      className="w-full rounded-xl border-2 border-brand-100 px-3 py-2.5 text-sm mt-1 focus:border-brand-500 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-brand-500">Deskripsi Kustom (opsional)</label>
                    <textarea
                      value={descOverride}
                      onChange={(e) => setDescOverride(e.target.value)}
                      placeholder="Deskripsi override..."
                      rows={2}
                      className="w-full rounded-xl border-2 border-brand-100 px-3 py-2.5 text-sm mt-1 focus:border-brand-500 focus:outline-none resize-none"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-brand-500">
                      Foto bukti kerja (opsional, 1 foto)
                    </label>
                    <p className="text-xs text-brand-400 mt-0.5">
                      Contoh: hasil bersih rumah harian. JPEG/PNG/WebP, maks 5MB. Foto tampil di katalog customer.
                    </p>
                    {photoPreview ? (
                      <div className="relative mt-2 inline-block">
                        <img
                          src={photoPreview}
                          alt="Preview foto jasa"
                          className="h-28 w-28 rounded-xl object-cover border border-brand-100"
                        />
                        <button
                          onClick={() => { setPhotoFile(null); setPhotoPreview(null); }}
                          className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-white shadow grid place-items-center text-brand-500 hover:text-red-500"
                          title="Hapus foto"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ) : (
                      <label className="mt-2 flex items-center justify-center gap-2 rounded-xl border-2 border-dashed border-brand-200 px-3 py-4 text-sm text-brand-500 cursor-pointer hover:border-brand-400 transition">
                        <ImagePlus className="h-5 w-5" />
                        Pilih foto dari galeri / kamera
                        <input
                          type="file"
                          accept="image/jpeg,image/png,image/webp"
                          className="hidden"
                          onChange={(e) => handlePhotoSelect(e.target.files?.[0])}
                        />
                      </label>
                    )}
                  </div>
                  <button
                    onClick={handleAdd}
                    disabled={submitting}
                    className="btn-primary w-full"
                    data-testid="confirm-add-service"
                  >
                    {submitting ? "Menambahkan..." : "Tambahkan"}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
